import {globalScope} from './globalScope';
import {logger} from './Logger';
import {isReactNative} from './Utils';

/**
 * Error-first callback for HTTP POST responses.
 *
 * Follows the Node.js error-first convention: the first argument is `null` on success
 * or an `Error` on failure, the second is the parsed JSON response body or `null` on failure.
 *
 * The callback is guaranteed to be called exactly once on every completion path — success,
 * HTTP error, network error, JSON parse failure, or XHR unavailability — so it is safe
 * to wrap in a `Promise` without risk of the Promise never settling.
 *
 * @param error - `null` when the request succeeded, an `Error` describing the failure otherwise.
 * @param response - The parsed JSON response body on success, `null` on failure.
 *
 * @remarks
 * `null` is used instead of `undefined` because `null` signals intentional absence — "there is
 * no error" / "there is no response" — whereas `undefined` is ambiguous and could mean the value
 * was never set. This matches the Node.js error-first callback convention where `callback(null, data)`
 * is the idiomatic way to indicate success.
 */
type PostCallback = (error: Error | null, response: any | null) => void;

/**
 * Sends an HTTP POST request with JSON-serialized body using XMLHttpRequest
 * (or XDomainRequest as a legacy fallback for IE8/IE9).
 *
 * Designed for fire-and-forget analytics calls (no callback) as well as request-response flows
 * like license validation (with callback). When a callback is provided, it is guaranteed to be
 * called exactly once on every completion path.
 *
 * @param url - The endpoint URL to POST to.
 * @param body - The payload to send. Will be serialized via `JSON.stringify`.
 * @param callback - Optional error-first callback invoked when the request completes, see {@link PostCallback}.
 * @param async - Whether to send the request asynchronously. Defaults to `true`.
 *   Forced to `true` in React Native (no sync XHR support) and when using legacy XDomainRequest.
 */
export function post(url: string, body: any, callback?: PostCallback, async = true): void {
  let xhttp: any;
  let isLegacyXDomainRequestUsed = false;

  // Use "XMLHttpRequest" by default to make a POST request
  // What about "fetch" API?
  //  There is no reason to use "fetch" API, the only real gains from "fetch" are developer experience.
  //  The performance difference is negligible in practice. Both ultimately make the same HTTP request
  //  over the same network stack. The browser doesn't route fetch faster than XHR.
  if (globalScope.XMLHttpRequest) {
    xhttp = new globalScope.XMLHttpRequest();
    // check for the standard XMLHttpRequest support with CORS (IE10+, all others)
    if (!('withCredentials' in xhttp)) {
      // Context:
      //  In IE8/IE9, XMLHttpRequest exists but doesn't support CORS (has no withCredentials).
      //  That's exactly why XDomainRequest was invented. Without this check, IE8/IE9 would pick
      //  XMLHttpRequest and fail on cross-origin requests.
      xhttp = null;
    }
  }

  // Fallback for IE8/IE9 cross-domain AJAX requests, where there is no support for the standard XMLHttpRequest with CORS.
  if (!xhttp && globalScope.XDomainRequest) {
    // XDomainRequest was Internet Explorer's proprietary CORS object (IE8/IE9), Microsoft decided to come up with
    // their own CORS XHR instead of the standard CORS XMLHttpRequest (which is now used in IE10 and later).
    // Since IE10, XDomainRequest has been deprecated and since IE11 removed.
    xhttp = new globalScope.XDomainRequest();
    isLegacyXDomainRequestUsed = true;
  }

  // In practice, this should never happen because all modern environments support XMLHttpRequest, and we have a
  // fallback to XDomainRequest for older IE versions. But this is just a safety check to avoid runtime errors in
  // case we're running in some very unusual environment where neither is available.
  if (!xhttp) {
    const message = 'Failed to create POST request, XHR implementation is missing';
    logger.error(message);
    if (callback) {
      callback(new Error(message), null);
    }
    return;
  }

  xhttp.onload = () => {
    // ...with XDomainRequest: status is always 0, itself "onload" firing is the only indicator of success
    if (isLegacyXDomainRequestUsed) {
      if (callback) {
        try {
          callback(null, JSON.parse(xhttp.responseText));
        } catch (e) {
          const message = 'Failed to parse response';
          logger.error(message, e);
          callback(new Error(message + ': ' + String(e)), null);
        }
      }
      return;
    }

    // ...but with XMLHttpRequest we can check the status properly
    if (xhttp.status < 200 || xhttp.status >= 300) {
      const message = 'POST error response:' + xhttp.status + ': ' + xhttp.statusText;
      logger.error(message);
      if (callback) {
        callback(new Error(message), null);
      }
      return;
    }

    if (callback) {
      try {
        callback(null, JSON.parse(xhttp.responseText));
      } catch (e) {
        const message = 'Failed to parse response';
        logger.error(message, e);
        callback(new Error(message + ': ' + String(e)), null);
      }
    }
  };

  xhttp.onerror = () => {
    const message = 'POST request failed: ' + xhttp.status + ' ' + xhttp.statusText;
    logger.error(message);
    if (callback) {
      callback(new Error(message), null);
    }
  };

  try {
    let openAsyncRequest = async;

    // React Native doesn't support synchronous XMLHttpRequest
    if (isReactNative() && !openAsyncRequest) {
      openAsyncRequest = true; // Force async mode in React Native environments
    }
    // Legacy XDomainRequest doesn't support synchronous requests
    // Passing false will either be ignored or throw an error depending on the IE version.
    if (isLegacyXDomainRequestUsed) {
      openAsyncRequest = true; // Force async mode when using legacy XDomainRequest
    }

    xhttp.open('POST', url, openAsyncRequest);

    // Legacy XDomainRequest doesn't support custom headers to be set
    // https://learn.microsoft.com/en-us/archive/blogs/ieinternals/xdomainrequest-restrictions-limitations-and-workarounds
    if (!isLegacyXDomainRequestUsed) {
      // setRequestHeader() must be called AFTER open() and BEFORE send() — calling it before open() will throw an InvalidStateError.
      xhttp.setRequestHeader('Content-Type', 'text/plain');
    }

    xhttp.send(JSON.stringify(body));
  } catch (e) {
    const message = 'Failed to send POST request: ' + String(e);
    logger.error(message);
    if (callback) {
      callback(new Error(message), null);
    }
  }
}
